// The v3 API keys and their IP whitelists, shared by the API and the SPA (README → Monorepo):
// what the portal's Settings → API Keys page sends and shows. A field renamed here fails both
// typechecks. The key itself appears in exactly one shape, IssuedApiKey (Security 1).
import { z } from "zod";

/** What a key may do on /v3 (Security 12): `read` keys are refused anything but GET. */
export const API_KEY_SCOPES = ["full", "read"] as const;
export const ApiKeyScope = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof ApiKeyScope>;

/** A rotated-out key stays `active` with an `expiresAt` 24 hours on, then stops working. */
export const API_KEY_STATUSES = ["active", "revoked"] as const;
export const ApiKeyStatus = z.enum(API_KEY_STATUSES);
export type ApiKeyStatus = z.infer<typeof ApiKeyStatus>;

/** How many keys a reseller may hold that still work, counting rotated-out ones in their overlap. */
export const MAX_ACTIVE_API_KEYS = 10;
/** How many addresses or ranges one key's whitelist may hold. */
export const MAX_WHITELIST_ENTRIES = 20;

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

export const IssueApiKeyRequest = z.object({
  name: z.string().trim().min(1).max(100),
  scope: ApiKeyScope,
});
export type IssueApiKeyRequest = z.infer<typeof IssueApiKeyRequest>;

export const ApiKey = z.object({
  id: Base62Id,
  name: z.string(),
  /** `kd_live_` and the first 6 random characters: for recognising a key, never for finding one. */
  prefix: z.string(),
  scope: ApiKeyScope,
  status: ApiKeyStatus,
  /** Set on a rotated-out key: when it stops working. */
  expiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type ApiKey = z.infer<typeof ApiKey>;

/** The one response that carries the key. Shown once; there is no way to read it again. */
export const IssuedApiKey = ApiKey.extend({ key: z.string() });
export type IssuedApiKey = z.infer<typeof IssuedApiKey>;

export const ListApiKeysResponse = z.object({ data: z.array(ApiKey) });
export const IssuedApiKeyResponse = z.object({ data: IssuedApiKey });
export const ApiKeyResponse = z.object({ data: ApiKey });

/**
 * A key's whitelist, replaced whole. Entries are single addresses or CIDR ranges no wider
 * than /24 (IPv4) or /64 (IPv6); the API checks the width (Security 10).
 */
export const ReplaceWhitelistRequest = z.object({
  entries: z.array(z.string().trim().min(1).max(64)).max(MAX_WHITELIST_ENTRIES),
});
export type ReplaceWhitelistRequest = z.infer<typeof ReplaceWhitelistRequest>;

export const WhitelistResponse = z.object({ data: z.object({ entries: z.array(z.string()) }) });
export type Whitelist = z.infer<typeof WhitelistResponse>["data"];
